import { CancelIcon } from '@/assets/icons'
import LocationAutocomplete, { PlaceSuggestion } from '@components/LocationAutocomplete'
import { EventCategory } from '@orbit/api-types'
import React from 'react'
import {
    ButtonGroup,
    CancelButton,
    FormContainer,
    FormGroup,
    Input,
    Label,
    Select,
    SubmitButton,
    TextArea,
} from './IdeaForm.styles'

const EVENT_CATEGORIES: EventCategory[] = [
	'FoodAndDrink',
	'SeeAndDo',
	'Nature',
	'Shopping',
	'Accommodation',
	'Transportation',
	'Event',
	'Other',
]

const EVENT_CATEGORY_LABELS: Record<EventCategory, string> = {
	FoodAndDrink: 'Food & Drink',
	SeeAndDo: 'See & Do',
	Nature: 'Nature',
	Shopping: 'Shopping',
	Accommodation: 'Accommodation',
	Transportation: 'Transportation',
	Event: 'Events',
	Other: 'Other',
}

interface IdeaFormProps {
	name: string
	description: string
	category: EventCategory | undefined
	locationPrimaryText: string
	locationSecondaryText?: string
	links: string[]
	onNameChange: (value: string) => void
	onDescriptionChange: (value: string) => void
	onCategoryChange: (value: EventCategory | undefined) => void
	onLocationPrimaryTextChange: (value: string) => void
	onLocationSelect: (place: PlaceSuggestion) => void
	onLinksChange: (value: string[]) => void
	onSubmit: (e: React.FormEvent) => void
	onCancel: () => void
	submitButtonText: string
	submitDisabled: boolean
}

const IdeaForm: React.FC<IdeaFormProps> = ({
	name,
	description,
	category,
	locationPrimaryText,
	locationSecondaryText,
	links,
	onNameChange,
	onDescriptionChange,
	onCategoryChange,
	onLocationPrimaryTextChange,
	onLocationSelect,
	onLinksChange,
	onSubmit,
	onCancel,
	submitButtonText,
	submitDisabled,
}) => {
	const handleLinkChange = (index: number, value: string) => {
		const newLinks = [...links]
		newLinks[index] = value
		onLinksChange(newLinks)
	}

	const handleAddLink = () => {
		onLinksChange([...links, ''])
	}

	const handleRemoveLink = (index: number) => {
		const newLinks = links.filter((_, i) => i !== index)
		onLinksChange(newLinks)
	}

	return (
		<FormContainer onSubmit={onSubmit}>
	<FormGroup>
		<Label htmlFor="idea-location">Location (Optional)</Label>
		<LocationAutocomplete
			id="idea-location"
			value={locationPrimaryText}
			onChange={onLocationPrimaryTextChange}
			onPlaceSelect={onLocationSelect}
			placeholder="Search for a location..."
			autoFocus
		/>
		{locationSecondaryText && (
			<div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.5rem', paddingLeft: '0.25rem' }}>
				{locationSecondaryText}
			</div>
		)}
	</FormGroup>

		<FormGroup>
			<Label htmlFor="idea-name">Name *</Label>
			<Input
				id="idea-name"
				type="text"
				value={name}
				onChange={e => onNameChange(e.target.value)}
				placeholder="Enter idea name"
				autoComplete="off"
				data-1p-ignore
				required
			/>
		</FormGroup>

			<FormGroup>
				<Label htmlFor="idea-category">Category</Label>
				<Select
					id="idea-category"
					value={category || EVENT_CATEGORIES[0]}
					onChange={e => onCategoryChange(e.target.value as EventCategory)}
					autoComplete="off"
				>
					{EVENT_CATEGORIES.map(cat => (
						<option key={cat} value={cat}>
							{EVENT_CATEGORY_LABELS[cat]}
						</option>
					))}
				</Select>
			</FormGroup>

			<FormGroup>
				<Label>Links</Label>
				{links.length > 0 && (
					<div style={{ marginBottom: '0.5rem' }}>
						{links.map((link, index) => (
							<div
								key={index}
								style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}
							>
								<Input
									type="text"
									placeholder="Enter a link or any text (e.g., https://example.com)"
									value={link}
									onChange={e => handleLinkChange(index, e.target.value)}
									autoComplete="off"
									style={{ flex: 1 }}
								/>
								<button
									type="button"
									onClick={() => handleRemoveLink(index)}
									style={{
										background: '#ef4444',
										color: 'white',
										border: 'none',
										borderRadius: '6px',
										padding: '0.25rem 0.5rem',
										cursor: 'pointer',
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'center',
									}}
									title="Remove link"
								>
									<CancelIcon width={18} height={18} fill="currentColor" />
								</button>
							</div>
						))}
					</div>
				)}
				<button
					type="button"
					onClick={handleAddLink}
					style={{
						background: 'white',
						color: '#374151',
						border: '1px solid #d1d5db',
						borderRadius: '6px',
						padding: '0.5rem 1rem',
						cursor: 'pointer',
						fontSize: '0.875rem',
						fontWeight: '500',
					}}
				>
					+ Add Link
				</button>
			</FormGroup>

			<FormGroup>
				<Label htmlFor="idea-description">Description</Label>
				<TextArea
					id="idea-description"
					value={description}
					onChange={e => onDescriptionChange(e.target.value)}
					placeholder="Add any notes or details about this idea"
					rows={4}
					autoComplete="off"
				/>
			</FormGroup>

			<ButtonGroup>
				<CancelButton type="button" onClick={onCancel}>
					Cancel
				</CancelButton>
				<SubmitButton type="submit" disabled={submitDisabled}>
					{submitButtonText}
				</SubmitButton>
			</ButtonGroup>
		</FormContainer>
	)
}

export default IdeaForm
